import { BehaviorConnection, BehaviorNode } from "../blueprint/types.js";
import {
  BehaviorGraph,
  LayoutDocument,
  LayoutListComponentProps,
  LayoutNode,
} from "../layout/types.js";
import { IndexedDocument, IndexedGraph } from "./types.js";

export function indexGraph(graph: BehaviorGraph): IndexedGraph {
  const nodesById = new Map<string, BehaviorNode>();
  const inputConnectionByNodeAndPort = new Map<string, BehaviorConnection>();
  const outputConnectionsByNodeAndPort = new Map<
    string,
    BehaviorConnection[]
  >();

  for (const node of graph?.nodes ?? []) {
    nodesById.set(node.id, node);
  }

  for (const connection of graph?.connections ?? []) {
    inputConnectionByNodeAndPort.set(
      `${connection.toNodeId}:${connection.toPortId}`,
      connection,
    );

    const outputKey = `${connection.fromNodeId}:${connection.fromPortId}`;
    const existing = outputConnectionsByNodeAndPort.get(outputKey) ?? [];
    existing.push(connection);
    outputConnectionsByNodeAndPort.set(outputKey, existing);
  }

  return {
    nodesById,
    inputConnectionByNodeAndPort,
    outputConnectionsByNodeAndPort,
  };
}

export function indexDocument(document: LayoutDocument): IndexedDocument {
  const graphsById = new Map<string, BehaviorGraph>();
  const nodesById = new Map<string, LayoutNode>();
  const parentById = new Map<string, string | null>();
  const childrenById = new Map<string, string[]>();

  function indexNode(node: LayoutNode, parentId: string | null = null) {
    nodesById.set(node.id, node);
    parentById.set(node.id, parentId);

    if (node.behavior) {
      graphsById.set(node.id, node.behavior);
    }

    if ("children" in node && Array.isArray(node.children)) {
      childrenById.set(
        node.id,
        node.children.map((child) => child.id),
      );

      for (const child of node.children) {
        indexNode(child, node.id);
      }
    }

    if (node.kind === "component" && node.component === "layoutList") {
      const props = node.props as LayoutListComponentProps | undefined;
      if (props?.rowTemplate) {
        indexNode(props.rowTemplate as LayoutNode, node.id);
      }

      for (const variant of props?.rowVariants ?? []) {
        indexNode(variant.template as LayoutNode, node.id);
      }
    }
  }

  for (const rootNode of document.scene.children) {
    indexNode(rootNode, null);
  }

  return {
    graphsById,
    nodesById,
    parentById,
    childrenById,
  };
}
